import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Outlet } from 'react-router-dom';
import { fetchGraphQL } from '@/relay/fetchGraphQL';
import { useAuth } from '@/contexts/AuthContext';

interface Workspace {
  id: string;
  name: string;
  role: string;
}

const workspaceRoleOrder: Record<string, number> = {
  OWNER: 0,
  ADMIN: 1,
  MEMBER: 2,
};

export function sortWorkspacesByRole(
  workspaces: ReadonlyArray<Workspace>,
): Workspace[] {
  return workspaces
    .map((workspace, index) => ({ workspace, index }))
    .sort((left, right) => {
      const leftOrder = workspaceRoleOrder[left.workspace.role.toUpperCase()] ?? 3;
      const rightOrder = workspaceRoleOrder[right.workspace.role.toUpperCase()] ?? 3;
      return leftOrder - rightOrder || left.index - right.index;
    })
    .map(({ workspace }) => workspace);
}

interface WorkspaceContextValue {
  workspaces: Workspace[];
  loading: boolean;
  activeWorkspaceId: string | null;
  isSwitching: boolean;
  refresh: () => Promise<void>;
  switchWorkspace: (workspaceId: string) => Promise<boolean>;
}

export const WorkspaceContext = createContext<WorkspaceContextValue | undefined>(
  undefined,
);

const VIEWER_QUERY = [
  'query ActiveWorkspaceViewer {',
  '  viewer {',
  '    activeWorkspaceId',
  '    workspaces {',
  '      id',
  '      name',
  '      viewerMembership { role }',
  '    }',
  '  }',
  '}',
].join('\n');

const SWITCH_WORKSPACE_MUTATION = [
  'mutation SwitchActiveWorkspace($workspaceId: ID!) {',
  '  switchActiveWorkspace(workspaceId: $workspaceId)',
  '}',
].join('\n');

interface ViewerResponse {
  data?: {
    viewer?: {
      activeWorkspaceId: string | null;
      workspaces: Array<{
        id: string;
        name: string;
        viewerMembership: { role: string } | null;
      }>;
    };
  };
  errors?: Array<{ message: string }>;
}

interface SwitchWorkspaceResponse {
  data?: {
    switchActiveWorkspace?: boolean;
  };
  errors?: Array<{ message: string }>;
}

export function WorkspaceProvider() {
  const {
    isAuthenticated,
    activeWorkspaceId,
    setActiveWorkspaceId,
  } = useAuth();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const activeWorkspaceIdRef = useRef(activeWorkspaceId);
  const switchVersionRef = useRef(0);
  const initialSyncRef = useRef(false);
  const explicitSelectionRef = useRef(false);

  activeWorkspaceIdRef.current = activeWorkspaceId;

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setWorkspaces([]);
      return;
    }

    const switchVersionAtStart = switchVersionRef.current;
    setLoading(true);

    try {
      const res = (await fetchGraphQL(
        { text: VIEWER_QUERY } as never,
        {},
      )) as ViewerResponse;
      const list = res.data?.viewer?.workspaces ?? [];
      const normalized = sortWorkspacesByRole(
        list.map((workspace) => ({
          id: workspace.id,
          name: workspace.name,
          role: workspace.viewerMembership?.role ?? 'MEMBER',
        })),
      );

      setWorkspaces(normalized);

      // A workspace switch that started while this request was in flight owns
      // the active selection. Do not let an older viewer response roll it
      // back.
      if (switchVersionAtStart !== switchVersionRef.current) {
        return;
      }

      const currentActive = activeWorkspaceIdRef.current;
      const currentIsValid =
        currentActive !== null &&
        normalized.some((workspace) => workspace.id === currentActive);
      const serverActive = res.data?.viewer?.activeWorkspaceId ?? null;
      const serverActiveIsValid =
        serverActive !== null &&
        normalized.some((workspace) => workspace.id === serverActive);

      let nextActive = currentIsValid
        ? currentActive
        : serverActiveIsValid
          ? serverActive
          : normalized[0]?.id ?? null;

      // On the initial synchronization, the server preference wins unless
      // the user has already explicitly selected a workspace in this session.
      if (!initialSyncRef.current && !explicitSelectionRef.current && serverActiveIsValid) {
        nextActive = serverActive;
      }

      initialSyncRef.current = true;
      if (nextActive !== currentActive) {
        activeWorkspaceIdRef.current = nextActive;
        setActiveWorkspaceId(nextActive);
      }
    } catch (err) {
      // Refresh failures should not discard the last known workspace list.
      // eslint-disable-next-line no-console
      console.warn('[workspace] refresh failed', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, setActiveWorkspaceId]);

  const switchWorkspace = useCallback(
    async (workspaceId: string) => {
      if (!workspaces.some((workspace) => workspace.id === workspaceId)) {
        throw new Error('Workspace is not available for this account');
      }

      if (activeWorkspaceIdRef.current === workspaceId) {
        return true;
      }

      explicitSelectionRef.current = true;
      switchVersionRef.current += 1;
      setIsSwitching(true);

      try {
        const response = (await fetchGraphQL(
          { text: SWITCH_WORKSPACE_MUTATION } as never,
          { workspaceId },
        )) as SwitchWorkspaceResponse;

        if (response.errors?.length) {
          throw new Error(response.errors[0]?.message ?? 'Could not switch workspace');
        }

        if (response.data?.switchActiveWorkspace !== true) {
          throw new Error('Could not switch workspace');
        }

        activeWorkspaceIdRef.current = workspaceId;
        setActiveWorkspaceId(workspaceId);
        return true;
      } finally {
        setIsSwitching(false);
      }
    },
    [setActiveWorkspaceId, workspaces],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <WorkspaceContext.Provider
      value={{
        workspaces,
        loading,
        activeWorkspaceId,
        isSwitching,
        refresh,
        switchWorkspace,
      }}
    >
      <Outlet />
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}
